import { useId } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { X } from "lucide-react";
import { TOUCH_SQUARE } from "@/components/buttonStyles";
import { GiaSection } from "@/components/GiaSection";
import type { BondSubmission } from "@/lib/brokerBonds";
import { isEndedStatus } from "@/lib/bondStateModel";

// BRK-S423 — the GIA Form dialog the submission page's GIA Form button opens:
// "General Indemnity Agreement · SB-##### · {insured}" over the GIA section
// itself (`GiaSection` in its `dialog` mode — the signer rows have one home),
// full screen on a phone. `?focus=gia` opens it, and the section lands the
// cursor once its signers load.
//
// Before PMS asks for a GIA on this order (no `gia` block: not yet approved,
// or referred) there is nothing to read, so the dialog says so and makes no
// request. On an order that has ended the section is read only: where the GIA
// stands, and nothing to send.

export function GiaFormDialog({
  order,
  open,
  focus = false,
  onClose,
  onChanged,
}: {
  order: Pick<
    BondSubmission,
    "id" | "number" | "accountLegalName" | "status" | "gia"
  >;
  open: boolean;
  /** `?focus=gia`: land the cursor in the section once it loads. */
  focus?: boolean;
  onClose: () => void;
  /** A GIA write answered 2xx: the page re-reads the order. */
  onChanged: () => void;
}) {
  const titleId = useId();
  const subId = useId();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={phone}
      scroll="paper"
      // "General Indemnity Agreement SB-##### · {insured}"
      aria-labelledby={`${titleId} ${subId}`}
    >
      <div className="flex items-start gap-3 border-b border-brk-border p-4">
        <div className="flex min-w-0 flex-1 flex-col">
          <h2 id={titleId} className="text-heading text-brk-text">
            General Indemnity Agreement
          </h2>
          <p id={subId} className="break-words text-sm text-brk-dim">
            {[order.number, order.accountLegalName]
              .filter((part): part is string => !!part)
              .join(" · ")}
          </p>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className={`rounded-md p-2 text-brk-dim hover:bg-brk-bg hover:text-brk-text ${TOUCH_SQUARE}`}
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>
      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        {order.gia ? (
          <GiaSection
            orderId={order.id}
            summary={order.gia}
            focus={focus}
            dialog={{
              onChanged,
              onClose,
              readOnly: isEndedStatus(order.status),
            }}
          />
        ) : (
          <p className="text-sm text-brk-text">
            No GIA has been asked for on this request yet. Every listed owner,
            and every listed owner’s spouse, signs one once the request is
            approved — we’ll send it then.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
